var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { KRFilePreview } from '../file-preview/file-preview.js';
/**
 * A file list component that displays files with extension-based icons,
 * download/delete actions, and built-in file preview.
 *
 * Clicking a file name opens a full-screen preview via `KRFilePreview.open()`.
 *
 * @fires file-click - Fired when a file name is clicked. Detail: `{ file }`
 * @fires download - Fired when the download icon is clicked. Detail: `{ file }`
 * @fires delete - Fired when the delete icon is clicked. Detail: `{ file }`
 *
 * @example
 * ```ts
 * import '@kodaris/krubble-components/file-list';
 *
 * const list = document.querySelector('kr-file-list');
 * list.files = [
 *   { name: 'photo.png', url: '/files/photo.png', date: '2024-01-15' },
 *   { name: 'report.pdf', url: '/files/report.pdf', date: '2024-01-10' },
 * ];
 * ```
 */
export class KRFileList extends LitElement {
    constructor() {
        super(...arguments);
        /** Array of file objects to display */
        this.files = [];
        /** Message displayed when the file list is empty */
        this.emptyMessage = 'No files';
    }
    _handleFileClick(file) {
        this.dispatchEvent(new CustomEvent('file-click', {
            bubbles: true,
            composed: true,
            detail: { file },
        }));
        if (file.url) {
            const preview = KRFilePreview.open({
                src: file.url,
                name: file.name
            });
            preview.addEventListener('download', () => {
                this._handleDownload(file);
            });
        }
    }
    _handleDownload(file) {
        this.dispatchEvent(new CustomEvent('download', {
            bubbles: true,
            composed: true,
            detail: { file },
        }));
    }
    _handleDelete(file) {
        this.dispatchEvent(new CustomEvent('delete', {
            bubbles: true,
            composed: true,
            detail: { file },
        }));
    }
    _renderExtIcon(ext) {
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) {
            return html `
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4.86 8.86-3 3.87L9 13.14 6 17h12l-3.86-5.14z"/>
        </svg>
      `;
        }
        if (['pdf'].includes(ext)) {
            return html `
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"/>
        </svg>
      `;
        }
        if (['doc', 'docx', 'rtf', 'txt'].includes(ext)) {
            return html `
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6zm2-6h8v2H8v-2zm0-4h8v2H8v-2zm0 8h5v2H8v-2z"/>
        </svg>
      `;
        }
        if (['xls', 'xlsx', 'csv'].includes(ext)) {
            return html `
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 2v3H5V5h14zm0 5v4H5v-4h14zM5 19v-3h14v3H5zm2-8h4v2H7v-2zm0 5h4v2H7v-2z"/>
        </svg>
      `;
        }
        if (['zip', 'rar', '7z', 'gz', 'tar'].includes(ext)) {
            return html `
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V6h5.17l2 2H20v10zm-8-4h2v2h-2v-2zm0-4h2v2h-2v-2zm-2 2h2v2h-2v-2z"/>
        </svg>
      `;
        }
        return html `
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/>
      </svg>
    `;
    }
    render() {
        if (!this.files.length) {
            return html `<div class="file-list__empty">${this.emptyMessage}</div>`;
        }
        return html `
      <div class="file-list">
        ${this.files.map(file => {
            const ext = file.name.split('.').pop()?.toLowerCase() || '';
            const metaText = file.meta || file.date || '';
            // The ext tile is colored by file family; anything unrecognized is default.
            let extClass = 'file-list__ext--default';
            if (['pdf'].includes(ext)) {
                extClass = 'file-list__ext--pdf';
            }
            if (['doc', 'docx', 'rtf', 'txt'].includes(ext)) {
                extClass = 'file-list__ext--doc';
            }
            if (['xls', 'xlsx', 'csv'].includes(ext)) {
                extClass = 'file-list__ext--xls';
            }
            if (['zip', 'rar', '7z', 'gz', 'tar'].includes(ext)) {
                extClass = 'file-list__ext--zip';
            }
            if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) {
                extClass = 'file-list__ext--img';
            }
            return html `
            <div class="file-list__item">
              <div
                class=${classMap({
                'file-list__ext': true,
                [extClass]: true
            })}
              >
                ${this._renderExtIcon(ext)}
              </div>
              <div class="file-list__info">
                <a
                  class="file-list__name"
                  @click=${() => this._handleFileClick(file)}
                >
                  ${file.name}
                </a>
                ${metaText ? html `<div class="file-list__meta">${metaText}</div>` : ''}
              </div>
              <div class="file-list__actions">
                <svg
                  class="file-list__action"
                  @click=${() => this._handleDownload(file)}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  title="Download"
                >
                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zm-8 2V5h2v6h1.17L12 13.17 9.83 11H11zm-6 7h14v2H5v-2z"/>
                </svg>
                <svg
                  class="file-list__action file-list__action--delete"
                  @click=${() => this._handleDelete(file)}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  title="Delete"
                >
                  <path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1zM18 7H6v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7z"/>
                </svg>
              </div>
            </div>
          `;
        })}
      </div>
    `;
    }
}
KRFileList.styles = css `
    *,
    *::after,
    *::before,
    :host {
      box-sizing: border-box;
    }

    :host {
      display: block;
    }

    .file-list {
      display: flex;
      flex-direction: column;
    }

    .file-list__item {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 16px 20px;
      background: white;
      transition: background 0.15s;
    }

    .file-list__item:not(:last-child) {
      border-bottom: 1px solid #eeeff1;
    }

    .file-list__item:hover {
      background: #f8f9fa;
    }

    .file-list__ext {
      width: 36px;
      height: 36px;
      flex-shrink: 0;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .file-list__ext svg {
      width: 20px;
      height: 20px;
    }

    .file-list__ext--img { background: #fde8e8; color: #d93025; }
    .file-list__ext--pdf { background: #fde8e8; color: #c0392b; }
    .file-list__ext--doc { background: #dbeafe; color: #2563eb; }
    .file-list__ext--xls { background: #d1fae5; color: #16a34a; }
    .file-list__ext--zip { background: #fef3c7; color: #b45309; }
    .file-list__ext--default { background: #f4f5f7; color: #555; }

    .file-list__info {
      flex: 1;
      min-width: 0;
    }

    .file-list__name {
      font-weight: 500;
      color: #000;
      text-decoration: none;
      cursor: pointer;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
      font-size: 14px;
    }

    .file-list__name:hover {
      color: #000;
      text-decoration: underline;
    }

    .file-list__meta {
      font-size: 13px;
      color: #000;
      margin-top: 6px;
    }

    .file-list__actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
    }

    .file-list__action {
      width: 24px;
      height: 24px;
      fill: #666;
      cursor: pointer;
      transition: fill 0.15s;
    }

    .file-list__action:hover {
      fill: #000;
    }


    .file-list__empty {
      color: #000;
      font-size: 14px;
      font-style: italic;
      padding: 20px;
    }
  `;
__decorate([
    property({ type: Array })
], KRFileList.prototype, "files", void 0);
__decorate([
    property({
        type: String,
        attribute: 'empty-message',
    })
], KRFileList.prototype, "emptyMessage", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-file-list')) {
    customElements.define('kr-file-list', KRFileList);
}
//# sourceMappingURL=file-list.js.map